// src/pages/PracticeHub.jsx
// Piano With Pan — Practice hub.
// Landing for /practice: pick Free Practice (the open practice game) or one of
// the note lessons (CDE / F&G / Note A — the existing Learn-Your-Notes part
// pages). All options are free and always unlocked; this is practice, not the
// gated journey. The how-to video lives on the CDE lesson only.

import React from "react";
import { useNavigate } from "react-router-dom";

const OPTIONS = [
  {
    key: "free",
    title: "Free Practice",
    subtitle: "Play any note, any time",
    icon: "🎹",
    to: "/practice/free",
    accent: { bg: "linear-gradient(135deg, #38bdf8, #0284c7)", ring: "#075985", glow: "rgba(56,189,248,0.32)" },
  },
  {
    key: "cde",
    title: "CDE",
    subtitle: "Learn C, D & E",
    icon: "🎵",
    to: "/learn-your-notes/a",
    accent: { bg: "linear-gradient(135deg, #EF4444, #DC2626)", ring: "#991B1B", glow: "rgba(239,68,68,0.32)" },
  },
  {
    key: "fg",
    title: "F & G",
    subtitle: "Now F and G",
    icon: "🎶",
    to: "/learn-your-notes/b",
    accent: { bg: "linear-gradient(135deg, #3B82F6, #2563EB)", ring: "#1E40AF", glow: "rgba(59,130,246,0.32)" },
  },
  {
    key: "a",
    title: "Note A",
    subtitle: "A + Twinkle Twinkle",
    icon: "⭐",
    to: "/learn-your-notes/c",
    accent: { bg: "linear-gradient(135deg, #EC4899, #DB2777)", ring: "#9D174D", glow: "rgba(236,72,153,0.32)" },
  },
];

function OptionNode({ opt, onClick }) {
  return (
    <button type="button" className="ph-node" onClick={onClick} aria-label={`${opt.title}: ${opt.subtitle}`}>
      <div
        className="ph-circle"
        style={{
          background: opt.accent.bg,
          borderColor: opt.accent.ring,
          boxShadow: `0 6px 0 ${opt.accent.ring}, 0 10px 22px ${opt.accent.glow}`,
        }}
      >
        <span className="ph-circle-icon">{opt.icon}</span>
      </div>
      <div className="ph-node-label">
        <div className="ph-node-title">{opt.title}</div>
        <div className="ph-node-sub">{opt.subtitle}</div>
      </div>
    </button>
  );
}

export default function PracticeHub() {
  const nav = useNavigate();

  return (
    <div className="ph-root">
      <style>{CSS}</style>

      <div className="ph-bg">
        <div className="ph-bg-base" />
        <div className="ph-blob ph-blob-1" />
        <div className="ph-blob ph-blob-2" />
      </div>

      <header className="ph-header">
        <button type="button" className="ph-hdr-btn" onClick={() => nav("/game-menu")} aria-label="Back to game menu">←</button>
        <div className="ph-hdr-title">
          <span className="ph-hdr-emoji">🎹</span>
          <span>Practice</span>
        </div>
        <div style={{ width: 36 }} />
      </header>

      <main className="ph-main">
        <div className="ph-intro">
          <div className="ph-intro-title">Pick how you'd like to practice</div>
          <div className="ph-intro-sub">Free play, or warm up a few notes at a time. No pressure — practice as much as you like.</div>
        </div>

        <div className="ph-path">
          {OPTIONS.map((opt, i) => (
            <React.Fragment key={opt.key}>
              {i > 0 && <div className="ph-connector" aria-hidden="true" />}
              <OptionNode opt={opt} onClick={() => nav(opt.to)} />
            </React.Fragment>
          ))}
        </div>
      </main>
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@600;700;800;900&display=swap');

.ph-root { min-height: 100dvh; font-family: 'Nunito', sans-serif; color: #0f172a; position: relative; overflow-x: hidden; }

.ph-bg { pointer-events: none; position: fixed; inset: 0; z-index: 0; overflow: hidden; }
.ph-bg-base { position: absolute; inset: 0; background: linear-gradient(180deg, #fffbeb 0%, #ffffff 60%, #f0fdfa 100%); }
.ph-blob { position: absolute; border-radius: 50%; filter: blur(100px); opacity: 0.3; animation: phFloat 20s infinite ease-in-out; }
.ph-blob-1 { top: -5%; left: -8%; width: 400px; height: 400px; background: linear-gradient(135deg, #bae6fd, #38bdf8); }
.ph-blob-2 { bottom: -10%; right: -5%; width: 360px; height: 360px; background: linear-gradient(135deg, #c4b5fd, #8b5cf6); animation-delay: 4s; }
@keyframes phFloat { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(15px,-20px) scale(1.04); } }

.ph-header {
  position: sticky; top: 0; z-index: 100;
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px; background: rgba(255,255,255,0.92);
  backdrop-filter: blur(14px); border-bottom: 1px solid #f1f5f9;
}
.ph-hdr-btn {
  width: 36px; height: 36px; border-radius: 50%; border: 2px solid #e2e8f0; background: #fff;
  font-size: 1rem; font-weight: 800; color: #0f172a; cursor: pointer; transition: all 0.15s;
  display: flex; align-items: center; justify-content: center;
}
.ph-hdr-btn:hover { border-color: #0f172a; transform: translateY(-1px); }
.ph-hdr-title { display: flex; align-items: center; gap: 8px; font-family: 'Fredoka One', cursive; font-size: 1rem; color: #0f172a; }
.ph-hdr-emoji { font-size: 1.1rem; }

.ph-main { position: relative; z-index: 1; padding: 24px 16px 80px; max-width: 460px; margin: 0 auto; display: flex; flex-direction: column; align-items: stretch; gap: 18px; }
.ph-intro { text-align: center; margin-top: 6px; }
.ph-intro-title { font-family: 'Fredoka One', cursive; font-size: 1.4rem; color: #0f172a; margin-bottom: 4px; }
.ph-intro-sub { font-size: 0.85rem; font-weight: 700; color: #64748b; max-width: 340px; margin: 0 auto; }

.ph-path { display: flex; flex-direction: column; align-items: center; gap: 0; padding: 12px 0; }
.ph-connector { width: 4px; height: 30px; border-radius: 2px; background: repeating-linear-gradient(to bottom, #86efac 0px, #86efac 6px, transparent 6px, transparent 12px); opacity: 0.8; }

.ph-node { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 6px 0; background: transparent; border: none; cursor: pointer; transition: transform 0.2s; }
.ph-node:hover { transform: translateY(-2px); }
.ph-circle {
  position: relative; width: 112px; height: 112px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; flex-shrink: 0; border: 5px solid;
  transition: transform 0.2s, box-shadow 0.2s;
}
.ph-circle-icon { font-size: 2.6rem; line-height: 1; }
.ph-node-label { text-align: center; }
.ph-node-title { font-family: 'Fredoka One', cursive; font-size: 1.1rem; color: #0f172a; line-height: 1.15; }
.ph-node-sub { font-size: 0.78rem; font-weight: 700; color: #64748b; margin-top: 2px; }

@media (max-width: 380px) {
  .ph-circle { width: 96px; height: 96px; border-width: 4px; }
  .ph-circle-icon { font-size: 2.2rem; }
}
`;